import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuth } from '@/lib/auth'
import { useFilial } from '@/lib/filial'
import {
  Lock, Unlock, FileText, Filter, X, Loader2, ArrowDownToLine, ArrowUpFromLine,
  CheckCircle2, AlertCircle, ChevronRight, User, Calendar, Wallet,
  BarChart3, Download, Printer, Store, Banknote, Sparkles, TrendingDown, Layers, Undo2,
  CreditCard, Smartphone, Landmark, Receipt, Pencil,
} from 'lucide-react'
import { api, formatMoney } from '@/lib/api'
import { usePdfPreview } from '@/components/PdfPreviewModal'
import EditarVendaModal from '@/components/EditarVendaModal'
import { formaVisual } from '@/lib/formaVisual'

interface SessaoListItem {
  id: number
  conta_id: number
  conta_nome?: string
  usuario_id: number
  usuario_nome?: string
  status: 'aberta' | 'fechada'
  valor_abertura: number
  valor_sistema: number | null
  outros_meios?: number | null      // pix/cartão recebidos (fora da gaveta de dinheiro)
  recebido_em_caixa?: number | null // total recebido agora (todas as formas)
  valor_contado: number | null
  diferenca: number | null
  aberto_em: string | null
  fechado_em: string | null
  // FEATURE 02/07: auditoria de reabertura do fechamento
  reaberto_em?: string | null
  reaberto_por_nome?: string | null
  motivo_reabertura?: string | null
  qtd_reaberturas?: number
}

function formatDateTime(s: string | null): string {
  if (!s) return '—'
  try { return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) }
  catch { return s }
}

export default function CaixaSessoesPage() {
  const [filtros, setFiltros] = useState<{
    status: string; conta_id: string; data_de: string; data_ate: string;
  }>({ status: '', conta_id: '', data_de: '', data_ate: '' })
  const [detalheId, setDetalheId] = useState<number | null>(null)
  const [aba, setAba] = useState<'sessoes' | 'relatorio'>('sessoes')
  const [reabrir, setReabrir] = useState<SessaoListItem | null>(null)  // FEATURE 02/07: reabrir fechamento
  const { abrir: abrirPdf, modal: pdfModal } = usePdfPreview()
  const qc = useQueryClient()

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (filtros.status) p.status = filtros.status
    if (filtros.conta_id) p.conta_id = filtros.conta_id
    if (filtros.data_de) p.data_de = filtros.data_de
    if (filtros.data_ate) p.data_ate = filtros.data_ate
    return p
  }, [filtros])

  const { data: contas = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  // FIX 02/07: filial na queryKey → a lista refaz ao trocar de filial (o backend
  // escopa por X-Filial-Id; cada filial só vê o próprio caixa). Sem isso o cache
  // ficava preso à filial anterior ao trocar.
  const filialListaId = useFilial(s => s.filialAtual?.id)
  const { data: sessoes = [], isLoading } = useQuery<SessaoListItem[]>({
    queryKey: ['caixa-sessoes', params, filialListaId],
    queryFn: async () => (await api.get('/caixa/sessoes', { params })).data,
  })

  const totais = useMemo(() => {
    const fechadas = sessoes.filter(s => s.status === 'fechada')
    const abertas = sessoes.filter(s => s.status === 'aberta').length
    const total_contado = fechadas.reduce((a, s) => a + (s.valor_contado || 0), 0)
    const total_diff = fechadas.reduce((a, s) => a + (s.diferenca || 0), 0)
    const com_falta = fechadas.filter(s => (s.diferenca || 0) < 0).length
    return { abertas, fechadas: fechadas.length, total_contado, total_diff, com_falta }
  }, [sessoes])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-ink-900">Caixa</h2>
          <p className="text-sm text-ink-500 mt-0.5">Histórico de sessões e relatório consolidado por período/filial.</p>
        </div>
      </div>

      {/* Abas */}
      <div className="flex items-center gap-1 border-b border-ink-200">
        {([['sessoes', 'Sessões', Lock], ['relatorio', 'Relatório Consolidado', BarChart3]] as const).map(([k, lbl, Ic]) => (
          <button key={k} onClick={() => setAba(k)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              aba === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}>
            <Ic size={15} /> {lbl}
          </button>
        ))}
      </div>

      {aba === 'relatorio' && <RelatorioConsolidado />}

      {aba === 'sessoes' && (<>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Unlock} label="Sessões abertas" value={String(totais.abertas)} tone="emerald" />
        <KPI icon={Lock} label="Sessões fechadas" value={String(totais.fechadas)} tone="brand" />
        <KPI icon={Wallet} label="Total contado" value={formatMoney(totais.total_contado)} tone="ink" />
        <KPI icon={AlertCircle} label="Com falta de caixa" value={String(totais.com_falta)} tone={totais.com_falta > 0 ? 'rose' : 'ink'} />
      </div>

      {/* Filtros */}
      <div className="card card-body">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-ink-700">
          <Filter size={14} /> Filtros
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="label">Status</label>
            <select className="input" value={filtros.status}
              onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))}>
              <option value="">Todos</option>
              <option value="aberta">Abertas</option>
              <option value="fechada">Fechadas</option>
            </select>
          </div>
          <div>
            <label className="label">Caixa / Conta</label>
            <select className="input" value={filtros.conta_id}
              onChange={e => setFiltros(f => ({ ...f, conta_id: e.target.value }))}>
              <option value="">Todas</option>
              {contas.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="label">De</label>
            <input className="input" type="date" value={filtros.data_de}
              onChange={e => setFiltros(f => ({ ...f, data_de: e.target.value }))} />
          </div>
          <div>
            <label className="label">Até</label>
            <input className="input" type="date" value={filtros.data_ate}
              onChange={e => setFiltros(f => ({ ...f, data_ate: e.target.value }))} />
          </div>
          <div className="flex items-end">
            <button className="btn-secondary w-full" onClick={() => setFiltros({ status: '', conta_id: '', data_de: '', data_ate: '' })}>
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium text-ink-700">#</th>
                <th className="px-3 py-2.5 text-left font-medium text-ink-700">Status</th>
                <th className="px-3 py-2.5 text-left font-medium text-ink-700">Operador</th>
                <th className="px-3 py-2.5 text-left font-medium text-ink-700">Caixa</th>
                <th className="px-3 py-2.5 text-left font-medium text-ink-700">Aberto em</th>
                <th className="px-3 py-2.5 text-left font-medium text-ink-700">Fechado em</th>
                <th className="px-3 py-2.5 text-right font-medium text-ink-700">Abertura</th>
                <th className="px-3 py-2.5 text-right font-medium text-ink-700">Esperado <span className="text-[10px] font-normal text-ink-400">(dinheiro)</span></th>
                <th className="px-3 py-2.5 text-right font-medium text-ink-700">PIX/Cartão</th>
                <th className="px-3 py-2.5 text-right font-medium text-ink-700">Contado</th>
                <th className="px-3 py-2.5 text-right font-medium text-ink-700">Diferença</th>
                <th className="px-3 py-2.5 text-center font-medium text-ink-700">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {isLoading && (
                <tr><td colSpan={12} className="text-center py-10 text-ink-400">
                  <Loader2 size={20} className="animate-spin inline mr-2" />Carregando...
                </td></tr>
              )}
              {!isLoading && sessoes.length === 0 && (
                <tr><td colSpan={12} className="text-center py-10 text-ink-400">Nenhuma sessão encontrada.</td></tr>
              )}
              {sessoes.map(s => (
                <tr key={s.id}
                  onClick={() => setDetalheId(s.id)}
                  className="hover:bg-violet-50 cursor-pointer">
                  <td className="px-3 py-2 font-mono text-xs">#{s.id}</td>
                  <td className="px-3 py-2">
                    {s.status === 'aberta' ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Unlock size={11} /> Aberta
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-ink-100 text-ink-700 border border-ink-200">
                        <Lock size={11} /> Fechada
                      </span>
                    )}
                    {/* FEATURE 02/07: marca caixa que já foi reaberto (auditoria) */}
                    {(s.qtd_reaberturas || 0) > 0 && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                        title={`Reaberto ${s.qtd_reaberturas}× · ${s.reaberto_por_nome || ''}${s.motivo_reabertura ? ' — ' + s.motivo_reabertura : ''}`}>
                        <Undo2 size={9} /> reaberto{(s.qtd_reaberturas || 0) > 1 ? ` ${s.qtd_reaberturas}×` : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{s.usuario_nome || `#${s.usuario_id}`}</td>
                  <td className="px-3 py-2">{s.conta_nome || `#${s.conta_id}`}</td>
                  <td className="px-3 py-2 text-xs text-ink-600">{formatDateTime(s.aberto_em)}</td>
                  <td className="px-3 py-2 text-xs text-ink-600">{formatDateTime(s.fechado_em)}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(s.valor_abertura)}</td>
                  <td className="px-3 py-2 text-right">{s.valor_sistema != null ? formatMoney(s.valor_sistema) : '—'}</td>
                  <td className="px-3 py-2 text-right text-ink-600">{s.outros_meios != null ? formatMoney(s.outros_meios) : '—'}</td>
                  <td className="px-3 py-2 text-right font-medium">{s.valor_contado != null ? formatMoney(s.valor_contado) : '—'}</td>
                  <td className={`px-3 py-2 text-right font-medium ${
                    s.diferenca == null ? 'text-ink-400' :
                    s.diferenca === 0 ? 'text-emerald-600' :
                    s.diferenca > 0 ? 'text-sky-600' : 'text-rose-600'
                  }`}>
                    {s.diferenca != null ? formatMoney(s.diferenca) : '—'}
                  </td>
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setDetalheId(s.id)} title="Ver detalhe"
                        className="p-1.5 rounded hover:bg-brand-50 text-brand-700">
                        <ChevronRight size={14} />
                      </button>
                      <button onClick={() => abrirPdf(`/caixa/sessoes/${s.id}/relatorio.pdf`, `Fechamento — sessão #${s.id}`)}
                        title="Ver / imprimir PDF" className="p-1.5 rounded hover:bg-brand-50 text-brand-700">
                        <FileText size={14} />
                      </button>
                      {/* FEATURE 02/07: reabrir caixa fechado (exige senha+cargo+motivo no backend) */}
                      {s.status === 'fechada' && (
                        <button onClick={() => setReabrir(s)}
                          title="Reabrir caixa (senha de supervisor)" className="p-1.5 rounded hover:bg-amber-50 text-amber-700">
                          <Undo2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detalheId !== null && (
        <DetalheSessaoModal sessaoId={detalheId} onClose={() => setDetalheId(null)} />
      )}
      {pdfModal}
      </>)}

      {reabrir && (
        <ReabrirCaixaModal
          sessao={reabrir}
          onClose={() => setReabrir(null)}
          onDone={() => { setReabrir(null); qc.invalidateQueries({ queryKey: ['caixa-sessoes'] }) }}
        />
      )}
    </div>
  )
}

// FEATURE 02/07: reabrir um caixa FECHADO (estorno do fechamento) — senha de supervisor
// + motivo obrigatório. Backend (_autorizar_estorno) exige cargo admin/gerente/financeiro
// (operador pode com senha de supervisor), reverte a sobra/falta e volta a sessão pra aberta.
function ReabrirCaixaModal({ sessao, onClose, onDone }: { sessao: SessaoListItem; onClose: () => void; onDone: () => void }) {
  const [motivo, setMotivo] = useState('')
  const [senha, setSenha] = useState('')
  const reabrirMut = useMutation({
    mutationFn: async () => (await api.post(`/caixa/sessoes/${sessao.id}/reabrir`, { motivo, senha })).data,
    onSuccess: () => { toast.success('Caixa reaberto — reconte e feche de novo'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao reabrir caixa'),
  })
  // aviso se o fechamento é de outro dia (já pode estar em relatório/DRE)
  const retro = !!sessao.fechado_em && new Date(sessao.fechado_em).toDateString() !== new Date().toDateString()
  return (
    <div className="fixed inset-0 bg-ink-950/70 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Undo2 size={20} className="text-amber-700" /></div>
          <div>
            <h3 className="text-lg font-bold text-ink-900">Reabrir caixa #{sessao.id}</h3>
            <p className="text-xs text-ink-500">Estorna o fechamento p/ recontar e fechar de novo</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-4">
          O fechamento atual será desfeito (a sobra/falta lançada no financeiro é revertida) e a sessão volta a ficar <b>aberta</b>. Fica registrado quem reabriu e por quê — nada é apagado.
          {retro && <div className="mt-1.5 font-semibold">⚠ Fechado em outro dia — pode já constar em relatórios/DRE.</div>}
        </div>
        <div className="space-y-3">
          <div>
            <label className="label">Motivo da reabertura</label>
            <textarea className="input" rows={2} value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex: valor contado digitado errado" autoFocus />
          </div>
          <div>
            <label className="label">Senha de supervisor <span className="text-ink-400 font-normal">(admin, gerente ou financeiro)</span></label>
            <input className="input" type="password" autoComplete="new-password" value={senha} onChange={e => setSenha(e.target.value)} placeholder="Senha" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button className="btn-secondary flex-1" onClick={onClose}>Cancelar</button>
          <button className="btn-primary flex-1 !bg-amber-600 hover:!bg-amber-700"
            disabled={!motivo.trim() || !senha || reabrirMut.isPending}
            onClick={() => reabrirMut.mutate()}>
            {reabrirMut.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Reabrir caixa'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Relatório Consolidado de Caixa (por período + filial)
// ============================================================
function inicioMesStr(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA')  // YYYY-MM-DD local
}
function hojeStr(): string { return new Date().toLocaleDateString('en-CA') }

function RelatorioConsolidado() {
  // Só admin/gerente escolhe a filial (ou vê consolidado). Operador comum fica
  // travado na SUA filial (a do login). O backend também força isso (não confia
  // só no front), mas aqui a UX já reflete: select pra admin, campo fixo pro resto.
  const isAdmin = useAuth(s => {
    const u = s.user
    return u?.perfil === 'super_admin' || u?.nivel === 'admin' || u?.perfil === 'admin' || u?.nivel === 'gerente'
  })
  const filialAtual = useFilial(s => s.filialAtual)
  const [filtros, setFiltros] = useState(() => ({
    data_de: inicioMesStr(), data_ate: hojeStr(),
    filial_id: !isAdmin && filialAtual ? String(filialAtual.id) : '',
    conta_id: '',
  }))
  // Operador: mantém o filtro preso à filial atual (caso ela resolva depois).
  useEffect(() => {
    if (!isAdmin && filialAtual) setFiltros(f => ({ ...f, filial_id: String(filialAtual.id) }))
  }, [isAdmin, filialAtual?.id])
  const { abrir: abrirPdf, modal: pdfModal } = usePdfPreview()

  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais-rel-caixa'],
    queryFn: async () => (await api.get('/filiais')).data,
  })
  const { data: contas = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const params = useMemo(() => {
    const p: Record<string, string> = { data_de: filtros.data_de, data_ate: filtros.data_ate }
    if (filtros.filial_id) p.filial_id = filtros.filial_id
    if (filtros.conta_id) p.conta_id = filtros.conta_id
    return p
  }, [filtros])

  const { data, isLoading } = useQuery<any>({
    queryKey: ['caixa-consolidado', params],
    queryFn: async () => (await api.get('/caixa/relatorio-consolidado', { params })).data,
    enabled: !!filtros.data_de && !!filtros.data_ate && filtros.data_ate >= filtros.data_de,
  })

  const qs = new URLSearchParams(params).toString()
  const baixarCsv = async () => {
    try {
      const r = await api.get('/caixa/relatorio-consolidado.csv', { params, responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url; a.download = `caixa-consolidado-${filtros.data_de}-a-${filtros.data_ate}.csv`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    } catch { /* toast já tratado no interceptor */ }
  }

  const t = data?.totais
  const maxForma = Math.max(1, ...(data?.por_forma || []).map((f: any) => f.valor))
  const maxDia = Math.max(1, ...(data?.por_dia || []).map((d: any) => d.recebido_caixa))

  return (
    <div className="space-y-4">
      {/* Filtros + ações */}
      <div className="card card-body">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-ink-700">
          <Filter size={14} /> Período &amp; Filial
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <label className="label">De</label>
            <input className="input" type="date" value={filtros.data_de}
              onChange={e => setFiltros(f => ({ ...f, data_de: e.target.value }))} />
          </div>
          <div>
            <label className="label">Até</label>
            <input className="input" type="date" value={filtros.data_ate}
              onChange={e => setFiltros(f => ({ ...f, data_ate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Filial</label>
            {isAdmin ? (
              <select className="input" value={filtros.filial_id}
                onChange={e => setFiltros(f => ({ ...f, filial_id: e.target.value }))}>
                <option value="">Todas (consolidado)</option>
                {filiais.filter((f: any) => f.ativo !== false).map((f: any) =>
                  <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social || `Filial ${f.codigo}`}</option>)}
              </select>
            ) : (
              <input className="input bg-ink-50 text-ink-600 cursor-not-allowed" disabled
                title="Apenas administradores podem trocar de filial"
                value={filialAtual ? (filialAtual.nome_fantasia || filialAtual.razao_social || `Filial ${filialAtual.codigo}`) : 'Sua loja'} />
            )}
          </div>
          <div>
            <label className="label">Caixa / Conta</label>
            <select className="input" value={filtros.conta_id}
              onChange={e => setFiltros(f => ({ ...f, conta_id: e.target.value }))}>
              <option value="">Todas</option>
              {contas.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="flex gap-2 md:col-span-2">
            <button onClick={() => abrirPdf(`/caixa/relatorio-consolidado.pdf?${qs}`, 'Relatório Consolidado de Caixa')}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!data}>
              <Printer size={15} /> PDF
            </button>
            <button onClick={baixarCsv}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!data}>
              <Download size={15} /> Excel
            </button>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-ink-400">
          <Loader2 size={22} className="animate-spin inline mr-2" />Calculando relatório...
        </div>
      )}

      {data && t && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI icon={Banknote} label="Recebido em caixa" value={formatMoney(t.recebido_caixa)} tone="emerald" />
            <KPI icon={Wallet} label="A prazo (não-caixa)" value={formatMoney(t.a_prazo)} tone="ink" />
            {/* 03/07: bike na troca = desconto (não entra em receitas/caixa); só aparece quando houve. */}
            {t.troca_bike > 0 && (
              <KPI icon={Receipt} label="Bike na troca (desconto)" value={formatMoney(t.troca_bike)} tone="emerald" />
            )}
            <KPI icon={ArrowDownToLine} label="Despesas + saídas" value={formatMoney(t.despesas + t.saidas)} tone="rose" />
            <KPI icon={t.diferenca >= 0 ? CheckCircle2 : TrendingDown}
              label="Diferença (sobra/falta)" value={formatMoney(t.diferenca)}
              tone={t.diferenca < 0 ? 'rose' : 'emerald'} />
            <KPI icon={Lock} label="Sessões" value={`${t.qtd_sessoes} (${t.qtd_fechadas} fech.)`} tone="brand" />
            <KPI icon={Layers} label="Vendas" value={String(t.qtd_vendas)} tone="ink" />
            <KPI icon={BarChart3} label="Ticket médio" value={formatMoney(t.ticket_medio)} tone="brand" />
            <KPI icon={AlertCircle} label="Sessões com falta" value={String(t.qtd_sessoes_falta)}
              tone={t.qtd_sessoes_falta > 0 ? 'rose' : 'ink'} />
          </div>

          {/* Insights */}
          {data.insights?.length > 0 && (
            <div className="card card-body bg-gradient-to-br from-violet-50 to-brand-50 border border-violet-200">
              <div className="flex items-center gap-2 text-sm font-semibold text-violet-900 mb-2">
                <Sparkles size={15} /> Análise do período
              </div>
              <ul className="space-y-1">
                {data.insights.map((ins: string, i: number) => (
                  <li key={i} className="text-sm text-ink-700 flex items-start gap-2">
                    <ChevronRight size={14} className="mt-0.5 text-violet-500 shrink-0" /> {ins}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Por forma de pagamento (barras) */}
            <div className="card card-body">
              <h4 className="text-sm font-semibold text-ink-900 mb-3">Por forma de pagamento</h4>
              {data.por_forma.length === 0 ? (
                <p className="text-sm text-ink-400 italic">Sem receitas no período.</p>
              ) : (
                <div className="space-y-2.5">
                  {data.por_forma.map((f: any) => (
                    <div key={f.forma}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-ink-700">{f.label} <span className="text-ink-400">· {f.qtd}</span></span>
                        <span className="font-semibold text-ink-900">{formatMoney(f.valor)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-ink-100 overflow-hidden">
                        <div className="h-full rounded-full bg-brand-500" style={{ width: `${(f.valor / maxForma) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Por dia (barras) */}
            <div className="card card-body">
              <h4 className="text-sm font-semibold text-ink-900 mb-3">Recebido em caixa por dia</h4>
              {data.por_dia.length === 0 ? (
                <p className="text-sm text-ink-400 italic">Sem dados.</p>
              ) : (
                <div className="flex items-end gap-1.5 h-40">
                  {data.por_dia.map((d: any) => (
                    <div key={d.dia} className="flex-1 flex flex-col items-center justify-end h-full group" title={`${d.dia}: ${formatMoney(d.recebido_caixa)}`}>
                      <div className="w-full rounded-t bg-emerald-400 group-hover:bg-emerald-500 transition-colors"
                        style={{ height: `${Math.max(3, (d.recebido_caixa / maxDia) * 100)}%` }} />
                      <span className="text-[9px] text-ink-400 mt-1">{d.dia.slice(8, 10)}/{d.dia.slice(5, 7)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Por filial (consolidado) */}
          {data.por_filial.length > 1 && (
            <TabelaSimples
              titulo="Por filial" icon={Store}
              header={['Filial', 'Sessões', 'Vendas', 'Recebido', 'Despesas', 'Diferença']}
              rows={data.por_filial.map((f: any) => [
                f.nome, String(f.qtd_sessoes), String(f.qtd_vendas),
                formatMoney(f.recebido_caixa), formatMoney(f.despesas),
                <span key="d" className={f.diferenca < 0 ? 'text-rose-600' : f.diferenca > 0 ? 'text-sky-600' : 'text-emerald-600'}>{formatMoney(f.diferenca)}</span>,
              ])}
              alignRight={[3, 4, 5]} alignCenter={[1, 2]}
            />
          )}

          {/* Por operador */}
          <TabelaSimples
            titulo="Por operador" icon={User}
            header={['Operador', 'Sessões', 'Vendas', 'Receitas', 'Diferença']}
            rows={data.por_operador.map((o: any) => [
              o.nome, String(o.qtd_sessoes), String(o.qtd_vendas), formatMoney(o.receitas),
              <span key="d" className={o.diferenca < 0 ? 'text-rose-600' : o.diferenca > 0 ? 'text-sky-600' : 'text-emerald-600'}>{formatMoney(o.diferenca)}</span>,
            ])}
            alignRight={[3, 4]} alignCenter={[1, 2]}
          />

          {/* Sessões do período */}
          <TabelaSimples
            titulo="Sessões do período" icon={Lock}
            header={['#', 'Abertura', 'Filial', 'Operador', 'Recebido', 'Contado', 'Diferença']}
            rows={data.sessoes.map((s: any) => [
              `#${s.id}`, formatDateTime(s.aberto_em), s.filial_nome, s.operador,
              formatMoney(s.recebido_caixa),
              s.valor_contado != null ? formatMoney(s.valor_contado) : '—',
              s.diferenca != null
                ? <span key="d" className={s.diferenca < 0 ? 'text-rose-600' : s.diferenca > 0 ? 'text-sky-600' : 'text-emerald-600'}>{formatMoney(s.diferenca)}</span>
                : '—',
            ])}
            alignRight={[4, 5, 6]}
            empty="Nenhuma sessão no período."
          />
        </>
      )}
      {pdfModal}
    </div>
  )
}

function TabelaSimples({ titulo, icon: Icon, header, rows, alignRight = [], alignCenter = [], empty }: {
  titulo: string; icon?: any; header: string[]; rows: any[][];
  alignRight?: number[]; alignCenter?: number[]; empty?: string;
}) {
  const align = (i: number) => alignRight.includes(i) ? 'text-right' : alignCenter.includes(i) ? 'text-center' : 'text-left'
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-ink-200 font-semibold text-sm text-ink-900 flex items-center gap-2">
        {Icon && <Icon size={15} className="text-brand-600" />} {titulo}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr>{header.map((h, i) => <th key={i} className={`px-3 py-2 font-medium text-ink-700 ${align(i)}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {rows.length === 0 && (
              <tr><td colSpan={header.length} className="text-center py-6 text-ink-400">{empty || 'Sem dados.'}</td></tr>
            )}
            {rows.map((r, ri) => (
              <tr key={ri} className="hover:bg-ink-50/60">
                {r.map((c, ci) => <td key={ci} className={`px-3 py-1.5 ${align(ci)} ${ci === 0 ? 'font-medium text-ink-800' : 'text-ink-700'}`}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function KPI({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone: string }) {
  const colors: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    brand: 'bg-brand-50 text-brand-700 border-brand-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    ink: 'bg-ink-50 text-ink-700 border-ink-200',
  }
  return (
    <div className={`card card-body border ${colors[tone] || colors.ink}`}>
      <div className="flex items-start gap-3">
        <Icon size={18} className="mt-0.5 opacity-70" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide opacity-75">{label}</div>
          <div className="text-xl font-bold mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  )
}

// UI 02/07: cor/ícone por forma agora em módulo compartilhado (fonte única de cores),
// usado aqui e no modal de fechar caixa do PDV — ver import formaVisual acima.

/** Nome CADASTRADO da forma (config_formas_pagamento). As tabelas mostravam o
 *  CÓDIGO formatado, então "PIX MAQUINA" (código pix) aparecia como "Pix" e não
 *  dava pra distinguir da "PIX CHAVE". Hook próprio pra funcionar em qualquer
 *  componente desta tela — o React Query dedupe a chamada entre eles. */
function useLabelForma() {
  const { data: cfgFormas = [] } = useQuery<any[]>({
    queryKey: ['cfg-formas'],
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  return (f: string) =>
    (cfgFormas as any[]).find(c => c.forma === f)?.label || (f || '').replace(/_/g, ' ')
}

function DetalheSessaoModal({ sessaoId, onClose }: { sessaoId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['caixa-sessao-detalhe', sessaoId],
    queryFn: async () => (await api.get(`/caixa/sessoes/${sessaoId}`)).data,
  })
  const labelForma = useLabelForma()
  const { abrir: abrirPdf, modal: pdfModal } = usePdfPreview()
  const qc = useQueryClient()
  // UI 02/07: tema atual (dark = classe .app-dark no <html>) p/ escolher o par de cores
  // inline das colunas de forma de pagamento. Reativo o bastante: o modal re-renderiza
  // quando o tema é trocado (estado do Layout muda a árvore).
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('app-dark')
    // FIX (10/09, pedido do cliente): "Recebimento" = só ENTRADAS (a Movimentação —
  // sangria/suprimento/saída — agora tem tabela própria, à parte). Dinheiro usa o
  // valor CONTADO no fechamento (o que realmente tem na gaveta), não a soma calculada.
  const totalPorForma = (() => {
    const mapa: Record<string, { vendas: number; receb: number }> = {}
    Object.entries(data?.receitas_por_forma || {}).forEach(([forma, valor]: [string, any]) => {
      if (!mapa[forma]) mapa[forma] = { vendas: 0, receb: 0 }
      mapa[forma].vendas += Number(valor || 0)
    })
    ;(data?.movimentos || []).forEach((m: any) => {
      if (m.estornado_em || m.tipo !== 'entrada') return
      const forma = m.forma || 'dinheiro'
      if (!mapa[forma]) mapa[forma] = { vendas: 0, receb: 0 }
      mapa[forma].receb += Number(m.valor || 0)
    })
    // FIX (relatado 10/09): sem venda nem recebimento em dinheiro, a forma nunca
    // entrava no mapa e a linha sumia — mesmo havendo troco/sangria no caixa
    // (valor contado > 0). Garante a linha "Dinheiro" sempre que houver valor contado.
    if (data?.valor_contado != null && !mapa['dinheiro']) mapa['dinheiro'] = { vendas: 0, receb: 0 }
    return Object.entries(mapa)
      .map(([forma, v]) => {
        let total = v.vendas + v.receb
        if (forma === 'dinheiro' && data?.valor_contado != null) total = Number(data.valor_contado)
        return { forma, vendas: v.vendas, receb: v.receb, total }
      })
      .sort((a, b) => b.total - a.total)
  })()
  const totalGeral = totalPorForma.reduce((acc, f) => acc + f.total, 0)
  // FEATURE 30/06: estorno de movimento (só caixa aberto; motivo + senha; cargo).
  const [estorno, setEstorno] = useState<any | null>(null)
  const [motivoEst, setMotivoEst] = useState('')
  // FEATURE 03/07: editar/histórico da venda (clique no nº da venda).
  const [editarVenda, setEditarVenda] = useState<number | null>(null)
  const [senhaEst, setSenhaEst] = useState('')
  const estornar = useMutation({
    mutationFn: () => api.post(`/caixa/sessoes/${sessaoId}/movimentos/${estorno.id}/estornar`,
      { motivo: motivoEst, senha: senhaEst }),
    onSuccess: () => {
      toast.success('Movimento estornado')
      setEstorno(null); setMotivoEst(''); setSenhaEst('')
      qc.invalidateQueries({ queryKey: ['caixa-sessao-detalhe', sessaoId] })
      qc.invalidateQueries({ queryKey: ['caixa-sessoes'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao estornar'),
  })

  return (
    <div className="fixed inset-0 bg-ink-950/70 z-50 flex items-center justify-center p-4">
      {pdfModal}
      {/* UI 02/07: modal de detalhe da sessão alargado (max-w-3xl -> max-w-5xl) p/ dar espaço aos pedidos/nome/valores */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-bold text-ink-900">Sessão de Caixa #{sessaoId}</h3>
            <p className="text-xs text-ink-500">{data?.conta_nome} · {data?.usuario_nome}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => abrirPdf(`/caixa/sessoes/${sessaoId}/relatorio.pdf`, `Fechamento — sessão #${sessaoId}`)}
              className="btn-secondary text-xs flex items-center gap-1.5">
              <FileText size={14} /> Ver / imprimir PDF
            </button>
            <button onClick={onClose} className="p-2 rounded hover:bg-ink-100"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {isLoading && <div className="text-center py-10 text-ink-400"><Loader2 size={20} className="animate-spin inline mr-2" />Carregando...</div>}
          {data && (
            <>
              {/* Header dados */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info icon={Calendar} label="Aberto em" value={formatDateTime(data.aberto_em)} />
                <Info icon={Calendar} label="Fechado em" value={formatDateTime(data.fechado_em)} />
                <Info icon={User} label="Operador" value={data.usuario_nome || '—'} />
                <Info icon={Wallet} label="Caixa/Conta" value={data.conta_nome || '—'} />
              </div>

              {/* Resumo financeiro */}
              <div className="card card-body bg-ink-50">
                <h4 className="text-sm font-semibold text-ink-900 mb-2">Resumo financeiro</h4>
                <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-sm">
                  <Linha label="(+) Abertura" value={data.valor_abertura} />
                  <Linha label="(+) Receitas em dinheiro" value={data.receitas_dinheiro} />
                  <Linha label="(+) Suprimentos" value={data.suprimentos} />
                  {/* BUG (fix 01/07): faltavam Saídas e Entradas em dinheiro — o total já as
                      contava, então "Esperado" não batia com as linhas mostradas. */}
                  {!!data.entradas_dinheiro && <Linha label="(+) Entradas em dinheiro" value={data.entradas_dinheiro} />}
                  <Linha label="(−) Sangrias" value={-data.sangrias} />
                  <Linha label="(−) Saídas" value={-data.saidas} />
                  <Linha label="(=) Esperado em caixa" value={data.valor_sistema_atual} bold />
                  {data.valor_contado != null && <Linha label="(=) Valor contado" value={data.valor_contado} bold />}
                  {data.diferenca != null && (
                    <div className="col-span-2 flex justify-between items-center bg-white rounded p-2 border border-ink-200 mt-1">
                      <span className="font-semibold text-sm">Diferença</span>
                      <span className={`font-bold text-base ${
                        data.diferenca === 0 ? 'text-emerald-600' :
                        data.diferenca > 0 ? 'text-sky-600' : 'text-rose-600'
                      }`}>
                        {formatMoney(data.diferenca)}
                        {data.diferenca > 0 && ' (sobra)'}
                        {data.diferenca < 0 && ' (falta)'}
                        {data.diferenca === 0 && ' ✓'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* FEATURE 02/07: DETALHE POR VENDA (matriz venda × forma). Uma linha por
                  venda, uma coluna por forma de pagamento; rodapé com total + qtd por
                  forma. Resolve o caso do R$100 em dinheiro "escondido" dentro de uma
                  venda a prazo — agora aparece explícito na coluna Dinheiro da venda #26. */}
              <div className="card card-body">
                <h4 className="text-sm font-semibold text-ink-900 mb-1">Detalhe por venda</h4>
                <p className="text-xs text-ink-500 mb-2">
                  Quanto cada venda recebeu em cada forma. Vendas <span className="font-medium text-amber-700">divididas</span> (mais de uma forma) vêm marcadas — é onde some, por ex., o dinheiro de entrada dentro de uma venda a prazo.
                </p>
                {!data.receitas || data.receitas.length === 0 ? (
                  <p className="text-sm text-ink-500 italic">Sem vendas no período.</p>
                ) : (() => {
                  // Colunas = formas presentes, ordenadas pelo total (maior → menor).
                  const formasCols: string[] = Object.entries(data.receitas_por_forma || {})
                    .sort((a: any, b: any) => b[1] - a[1])
                    .map(([f]) => f)
                  const fmtForma = (f: string) => labelForma(f)
                  // UI 02/07: tabela COMPACTA p/ caber todas as formas sem rolar de lado —
                  // fonte text-xs + tabular-nums, padding apertado (px-1.5/py-1),
                  // cliente truncado curto (max-w-[120px]) com title p/ ver inteiro no hover.
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs whitespace-nowrap tabular-nums">
                        <thead className="text-[11px] text-ink-500 border-b">
                          <tr>
                            <th className="text-left py-1 pr-1.5 sticky left-0 bg-white">Venda</th>
                            <th className="text-left py-1 pr-2">Cliente</th>
                            {/* UI 02/07: header da forma com iconezinho + cor própria (inline, por tema) */}
                            {formasCols.map((f) => {
                              const fv = formaVisual(f); const Ic = fv.icon
                              const c = isDark ? fv.dark : fv.light
                              return (
                                <th key={f} className="text-right py-1 px-1.5" style={{ color: c.text, backgroundColor: c.bg }}>
                                  <span className="inline-flex items-center gap-1 justify-end">
                                    <Ic size={12} style={{ color: c.icon }} />{fmtForma(f)}
                                  </span>
                                </th>
                              )
                            })}
                            <th className="text-right py-1 pl-1.5">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-100">
                          {data.receitas.map((v: any, idx: number) => {
                            // UI 02/07: ZEBRA (linhas alternadas) p/ o olho traçar cliente→valor
                            // sem se perder nas colunas coloridas. Colunas neutras (venda/cliente/
                            // total) usam tokens bg-white/ink-100 (o CSS global .app-dark remapeia
                            // no escuro; ink-100 é mais escuro que white nos DOIS temas). Colunas de
                            // forma têm cor inline própria → alternância via overlay.
                            // FIX 02/07: overlay SEMPRE escurece (preto). Antes clareava no dark
                            // (branco) e ficava invertido — neutras escuras e forma clara na mesma
                            // linha. No dark precisa de mais alpha (cor da forma já é escura).
                            const zebra = idx % 2 === 1
                            const neutra = zebra ? 'bg-ink-100' : 'bg-white'
                            const overlay = zebra
                              ? (isDark ? 'linear-gradient(rgba(0,0,0,0.28),rgba(0,0,0,0.28))'
                                        : 'linear-gradient(rgba(0,0,0,0.09),rgba(0,0,0,0.09))')
                              : undefined
                            return (
                            <tr key={v.pedido_id}>
                              <td className={`py-1 pr-1.5 font-medium sticky left-0 ${neutra}`}>
                                <button onClick={() => setEditarVenda(v.pedido_id)}
                                  title="Editar / histórico da venda"
                                  className="text-brand-700 hover:underline inline-flex items-center gap-0.5">
                                  #{v.numero}<Pencil size={10} className="opacity-60" />
                                </button>
                                {v.dividida && (
                                  <span className="ml-1 text-[9px] font-semibold text-amber-700 bg-amber-100 px-1 rounded">dividida</span>
                                )}
                                {/* 03/07: bike na troca = desconto (não receita). Total da venda já
                                    vem líquido; badge mostra quanto foi de bike (valor no hover). */}
                                {!!v.bike_troca && v.bike_troca > 0 && (
                                  <span className="ml-1 text-[9px] font-semibold text-emerald-700 bg-emerald-100 px-1 rounded"
                                    title={`Bike recebida na troca: ${formatMoney(v.bike_troca)} (desconto — virou estoque seminovo)`}>🚲 troca</span>
                                )}
                              </td>
                              {/* cliente truncado curto p/ liberar largura aos valores; nome inteiro no title (hover) */}
                              <td className={`py-1 pr-2 text-ink-600 max-w-[120px] truncate ${neutra}`} title={v.cliente_nome}>{v.cliente_nome}</td>
                              {formasCols.map((f) => {
                                const val = v.formas_valores?.[f]
                                // UI 02/07: célula com a cor da forma (inline, por tema) p/ leitura por coluna
                                const c = isDark ? formaVisual(f).dark : formaVisual(f).light
                                return (
                                  <td key={f} className={`py-1 px-1.5 text-right ${val ? 'font-medium' : 'text-ink-300'}`} style={{ backgroundColor: c.bg, backgroundImage: overlay }}>
                                    {val ? formatMoney(val) : '—'}
                                  </td>
                                )
                              })}
                              <td className={`py-1 pl-1.5 text-right font-semibold ${neutra}`}>{formatMoney(v.total)}</td>
                            </tr>
                            )
                          })}
                        </tbody>
                        <tfoot className="border-t border-ink-200">
                          <tr>
                            <td className="py-1 pr-1.5 sticky left-0 bg-white font-semibold">TOTAL</td>
                            <td className="py-1 pr-2 text-ink-500 text-[11px]">{data.receitas.length} venda(s)</td>
                            {/* UI 02/07: rodapé também com a cor da forma (inline, por tema) */}
                            {formasCols.map((f) => {
                              const c = isDark ? formaVisual(f).dark : formaVisual(f).light
                              return (
                                <td key={f} className="py-1 px-1.5 text-right align-bottom" style={{ backgroundColor: c.bg }}>
                                  <div className="font-bold text-brand-700">{formatMoney(data.receitas_por_forma?.[f] || 0)}</div>
                                  <div className="text-[9px] text-ink-400 font-normal">{data.receitas_qtd_por_forma?.[f] ?? 0}×</div>
                                </td>
                              )
                            })}
                            <td className="py-1 pl-1.5 text-right font-bold text-brand-700">{formatMoney(data.receitas_total || 0)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )
                })()}
                {/* 03/07 (pedido do usuário): a bike recebida na troca é DESCONTO,
                    não receita — saiu do total de vendas (que agora é líquido) e
                    aparece aqui à parte, igual ao cupom. O que "entra" é a bike no
                    estoque (produto seminovo), não dinheiro no caixa. */}
                {!!data.troca_bike_total && data.troca_bike_total > 0 && (
                  <div className="mt-2 flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <span className="text-emerald-800 font-medium">🚲 Bike recebida na troca (desconto — não é receita/caixa)</span>
                    <span className="font-bold text-emerald-800">{formatMoney(data.troca_bike_total)}</span>
                  </div>
                )}
              </div>

                {/* FIX (10/09, pedido do cliente): Movimentação e Recebimento separados,
                  lado a lado, mesmo design nos dois. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card card-body">
                  <h4 className="text-sm font-semibold text-ink-900 mb-2">Movimentação</h4>
                  <TabelaMovimentosCaixa
                    itens={(data.movimentos || []).filter((m: any) => m.tipo === 'sangria' || m.tipo === 'saida' || m.tipo === 'suprimento')}
                    isDark={isDark} statusAberta={data.status === 'aberta'}
                    onEstornar={(m: any) => { setEstorno(m); setMotivoEst(''); setSenhaEst('') }}
                  />
                </div>
                <div className="card card-body">
                  <h4 className="text-sm font-semibold text-ink-900 mb-2">Recebimento</h4>
                  <TabelaMovimentosCaixa
                    itens={(data.movimentos || []).filter((m: any) => m.tipo === 'entrada')}
                    isDark={isDark} statusAberta={data.status === 'aberta'}
                    onEstornar={(m: any) => { setEstorno(m); setMotivoEst(''); setSenhaEst('') }}
                  />
                </div>
              </div>

                {/* FIX (09/09, pedido do cliente): Total Geral DETALHADO por forma —
                  venda e movimentação da mesma forma (ex.: cartão) somam na mesma linha */}
              <div className="card card-body">
                <h4 className="text-sm font-semibold text-ink-900 mb-2">Total Geral por Forma de Pagamento</h4>
                <table className="w-full text-sm">
                  <thead className="text-xs text-ink-500 border-b">
                    <tr>
                      <th className="text-left py-1.5">Forma</th>
                      <th className="text-right py-1.5">Vendas</th>
                      <th className="text-right py-1.5">Recebimento</th>
                      <th className="text-right py-1.5">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {totalPorForma.map(f => {
                      const fv = formaVisual(f.forma)
                      const fc = isDark ? fv.dark : fv.light
                      return (
                        <tr key={f.forma}>
                          <td className="py-1.5">
                            {/* sem capitalize: o label vem como cadastrado ("PIX MAQUINA") */}
                            <span className="inline-flex items-center gap-1 text-xs" style={{ color: fc.text }}>
                              <fv.icon size={11} style={{ color: fc.icon }} />{labelForma(f.forma)}
                            </span>
                          </td>
                          <td className="text-right py-1.5">{formatMoney(f.vendas)}</td>
                          <td className={`text-right py-1.5 ${f.receb > 0 ? 'text-sky-700' : 'text-ink-400'}`}>
                            {f.receb > 0 ? '+ ' + formatMoney(f.receb) : '—'}
                          </td>
                          <td className="text-right py-1.5 font-semibold">{formatMoney(f.total)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      {/* FIX (10/09, pedido do cliente): texto branco em fundo sólido —
                          text-brand-900/700 ficava ilegível no tema escuro (mesmo problema
                          do card antigo). Fundo sólido garante contraste nos dois temas. */}
                      <td colSpan={3} className="py-2.5 px-2 font-semibold text-white bg-brand-600 rounded-l-lg">Total Geral</td>
                      <td className="py-2.5 px-2 text-right font-bold text-white bg-brand-600 text-base rounded-r-lg">{formatMoney(totalGeral)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {data.observacao_abertura && (
                <div className="card card-body bg-amber-50 border border-amber-200">
                  <div className="text-xs font-medium text-amber-900 mb-1">Observação da abertura</div>
                  <div className="text-sm text-amber-900/90">{data.observacao_abertura}</div>
                </div>
              )}
              {data.observacao_fechamento && (
                <div className="card card-body bg-rose-50 border border-rose-200">
                  <div className="text-xs font-medium text-rose-900 mb-1">Observação do fechamento</div>
                  <div className="text-sm text-rose-900/90">{data.observacao_fechamento}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* FEATURE 30/06: modal de estorno (motivo + senha; exige cargo) */}
      {estorno && (
        <div className="fixed inset-0 bg-ink-950/70 z-[60] flex items-center justify-center p-4" onClick={() => setEstorno(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
              <h3 className="font-bold text-ink-900 flex items-center gap-2"><Undo2 size={18} className="text-rose-600" /> Estornar movimento</h3>
              <button onClick={() => setEstorno(null)} className="p-1.5 rounded hover:bg-ink-100"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm text-ink-700 bg-ink-50 rounded px-3 py-2">
                {estorno.descricao || estorno.tipo} — <strong>{formatMoney(estorno.valor)}</strong>
              </div>
              <div>
                <label className="text-xs text-ink-600">Motivo do estorno *</label>
                <textarea className="input w-full mt-1 text-sm" rows={2} autoFocus
                  value={motivoEst} onChange={e => setMotivoEst(e.target.value)}
                  placeholder="Ex.: lançado com valor errado" />
              </div>
              <div>
                <label className="text-xs text-ink-600">Senha *</label>
                {/* type=text + text-security: mascara visualmente SEM ser campo de senha,
                    assim o navegador/gerenciador NÃO oferece/preenche a senha salva do login. */}
                <input type="text" className="input w-full mt-1 text-sm" name="estorno-confirm"
                  autoComplete="off" data-lpignore="true" data-1p-ignore data-form-type="other"
                  style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' } as any}
                  value={senhaEst} onChange={e => setSenhaEst(e.target.value)}
                  placeholder="Sua senha (ou de um supervisor)" />
                <p className="text-[11px] text-ink-500 mt-1">Estorno exige cargo. Sem cargo, use a senha de um supervisor (admin, gerente ou financeiro).</p>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-ink-200 flex justify-end gap-2">
              <button onClick={() => setEstorno(null)} className="btn-secondary text-sm">Cancelar</button>
              <button
                onClick={() => estornar.mutate()}
                disabled={estornar.isPending || !motivoEst.trim() || !senhaEst}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
                {estornar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Undo2 size={15} />} Confirmar estorno
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE 03/07: editar venda / histórico (clique no nº da venda) */}
      {editarVenda != null && (
        <EditarVendaModal
          pedidoId={editarVenda}
          onClose={() => setEditarVenda(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['caixa-sessao-detalhe', sessaoId] })}
        />
      )}
    </div>
  )
}

// FIX (10/09, pedido do cliente): tabela reaproveitada nas colunas Movimentação e
// Recebimento — mesmo design, filtrada por tipo em cada lado.
function TabelaMovimentosCaixa({ itens, isDark, statusAberta, onEstornar }: {
  itens: any[]; isDark: boolean; statusAberta: boolean; onEstornar: (m: any) => void;
}) {
  const labelForma = useLabelForma()
  const total = itens.reduce((acc: number, m: any) => {
    if (m.estornado_em) return acc
    const sinal = (m.tipo === 'sangria' || m.tipo === 'saida') ? -1 : 1
    return acc + sinal * Number(m.valor || 0)
  }, 0)
  if (!itens.length) return <p className="text-sm text-ink-500 italic">Sem movimentos.</p>
  return (
    <table className="w-full text-sm">
      <thead className="text-xs text-ink-500 border-b">
        <tr>
          <th className="text-left py-1.5">Hora</th>
          <th className="text-left py-1.5">Tipo</th>
          <th className="text-left py-1.5">Forma</th>
          <th className="text-left py-1.5">Descrição</th>
          <th className="text-right py-1.5">Valor</th>
          <th className="text-right py-1.5"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-100">
        {itens.map((m: any) => {
          const saiDaGaveta = m.tipo === 'sangria' || m.tipo === 'saida'
          const tipoLabel = { sangria: 'Sangria', suprimento: 'Suprimento', saida: 'Saída', entrada: 'Entrada' }[m.tipo as string] || m.tipo
          const estornado = !!m.estornado_em
          const fv = m.forma ? formaVisual(m.forma) : null
          const fc = fv ? (isDark ? fv.dark : fv.light) : null
          return (
            <tr key={m.id} className={estornado ? 'opacity-60' : ''}>
              <td className="py-1.5 text-xs text-ink-600">
                {m.criado_em ? new Date(m.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
              </td>
              <td className="py-1.5">
                <span className="inline-flex items-center gap-1 text-xs">
                  {saiDaGaveta ? <ArrowDownToLine size={11} className="text-amber-600" /> : <ArrowUpFromLine size={11} className="text-sky-600" />}
                  {tipoLabel}
                </span>
              </td>
              <td className="py-1.5">
                {fv && fc ? (
                  <span className="inline-flex items-center gap-1 text-xs" style={{ color: fc.text }}>
                    <fv.icon size={11} style={{ color: fc.icon }} />{labelForma(m.forma)}
                  </span>
                ) : <span className="text-ink-300 text-xs">—</span>}
              </td>
              <td className="py-1.5 text-xs">
                <span className={estornado ? 'line-through' : ''}>{m.descricao || '—'}</span>
                {estornado && (
                  <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-100 px-1 rounded">
                    ESTORNADO{m.autorizado_por ? ` · ${m.autorizado_por}` : ''}
                  </span>
                )}
                {estornado && m.motivo_estorno && (
                  <div className="text-[10px] text-ink-400 mt-0.5">Motivo: {m.motivo_estorno}</div>
                )}
              </td>
              <td className={`py-1.5 text-right font-medium ${estornado ? 'line-through text-ink-400' : saiDaGaveta ? 'text-amber-700' : 'text-sky-700'}`}>
                {saiDaGaveta ? '−' : '+'} {formatMoney(m.valor)}
              </td>
              <td className="py-1.5 text-right">
                {!estornado && statusAberta && (
                  <button onClick={() => onEstornar(m)}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-ink-200 text-rose-700 hover:bg-rose-50">
                    <Undo2 size={11} /> Estornar
                  </button>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
      <tfoot className="border-t border-ink-200">
        <tr>
          <td colSpan={4} className="py-1.5 font-semibold text-ink-700">Total</td>
          <td className={`py-1.5 text-right font-semibold ${total < 0 ? 'text-rose-700' : 'text-sky-700'}`}>
            {total >= 0 ? '+ ' : '− '}{formatMoney(Math.abs(total))}
          </td>
          <td></td>
        </tr>
      </tfoot>
    </table>
  )
}

function Info({ icon: Icon, label, value }: any) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="mt-0.5 text-ink-400" />
      <div>
        <div className="text-xs text-ink-500">{label}</div>
        <div className="font-medium text-ink-900">{value}</div>
      </div>
    </div>
  )
}

function Linha({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <>
      <span className={bold ? 'font-semibold text-ink-900' : 'text-ink-600'}>{label}</span>
      <span className={`text-right ${bold ? 'font-bold text-ink-900' : 'font-medium'}`}>{formatMoney(value || 0)}</span>
    </>
  )
}
